import React, { useEffect } from "react";
import { Dialog } from "@material-ui/core";
import { CloseIcon } from "../../../../../assets/Icons";
import { formatNumber, removeKeys } from "../../../utils";
import RoundCloseIcon from "../../../../../assets/Icons/RoundCloseIcon";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import Button from "../../../../../components/ui/Button";
import { remittanceActions } from "../../../state/remittanceSlice";

interface Props {
  open: boolean;
  onYes: () => void;
  onNo: () => void;
}

const panRegExp = "[a-zA-Z0-9 ]{1,}";

export const PanDeclarationModal: React.FC<Props> = ({ open, onYes, onNo }) => {
  const dispatch = useAppDispatch();
  
  const { calculator } = useAppSelector((state) => state.remittance);

  const [panNumber, setPanNumber] = React.useState(calculator?.amountCalculation?.pan_number ?? "");
  const [declareAmount, setDeclareAmount] = React.useState(calculator?.amountCalculation?.declare_amount ?? "");

  useEffect(() => {
    setPanNumber(calculator?.amountCalculation?.pan_number);
  }, [calculator?.amountCalculation?.pan_number])
  
  useEffect(() => {
    setDeclareAmount(calculator?.amountCalculation?.declare_amount);
  }, [calculator?.amountCalculation?.declare_amount]);

  useEffect(() => {
    if(!panNumber) {
      setDeclareAmount("");
    }
  }, [panNumber]);

  const onCloseClick = () => {
    if(!calculator?.amountCalculation?.pan_number) {
      setPanNumber("");
    }
    if(!calculator?.amountCalculation?.declare_amount) {
      setDeclareAmount("");
    }
    onNo();
  }

  const onSubmitPanDeclaration = () => {
    dispatch(remittanceActions.setAmountCalculation({ pan_number: panNumber, declare_amount: declareAmount }));
    dispatch(remittanceActions.setModalType(""));
  }

  const checkPanNumber = () => {
    const isSend = calculator?.amountCalculation?.changeInputFrom === "send";
    const amountCalculation = {...calculator?.amountCalculation};
    const isUserSendORRecipient = isSend && !calculator?.amountCalculation?.lock_reciepient_recieve ? "reciepient_recieve" : "user_send";
    const keys = [isUserSendORRecipient, "changeInputFrom", "countryDropdown", "purposeDropdown", "vendorDropdown", "lock_reciepient_recieve", "skipApi"];
    const formData = removeKeys(amountCalculation, keys);
    formData.declare_amount = declareAmount || 0;
    formData.pan_number = panNumber;
    dispatch(remittanceActions.fetchPanDeclarationRequested(formData));
  }

  return (
    <Dialog open={open}>
      <div className="border rounded-lg overflow-hidden">
        <div className="flex flex-row justify-between items-center px-5 pt-2">
          <div className="font-bold text-[22px] text-center text-primary-1050">TCS Declaration</div>
          <CloseIcon 
            className="cursor-pointer" 
            onClick={onCloseClick} 
          />
        </div>

        <div className="flex flex-col items-left px-6 w-[500px]">

          <span className="font-medium text-[16px] mt-2">PAN</span>

          {/* FIXED PAN INPUT ROW */}
          <div className="flex h-10 flex-row items-center w-full mt-2 border border-gray-200 rounded-lg px-2 py-1">
            <input
              className={`flex-1 outline-none`}
              type="text"
              value={panNumber}
              onChange={(e) => {
                setPanNumber(e.target.value.toUpperCase());
                dispatch(remittanceActions.resetPanDeclaration());
              }}
              autoComplete="off"
              maxLength={10}
              pattern={panRegExp}
            />

            {panNumber && (
              <button
                className="px-1"
                onClick={() => {
                  setPanNumber("");
                  dispatch(remittanceActions.resetPanDeclaration());
                  dispatch(remittanceActions.setAmountCalculation({ pan_number: "", declare_amount: "" }));
                }}
              >
                <RoundCloseIcon />
              </button>
            )}

            <button
              type="button"
              onClick={checkPanNumber}
              className={`px-3 py-1 bg-green-500 text-white rounded-lg outline-none ${
                panNumber?.length !== 10 || !panNumber?.match(panRegExp)
                  ? "opacity-50 cursor-not-allowed"
                  : "opacity-100 cursor-pointer"
              }`}
              disabled={panNumber?.length !== 10 || !panNumber?.match(panRegExp)}
            >
              <div className="flex items-center justify-center w-12">
                {calculator?.getPanDeclaration?.loading ? (
                  <div className="animate-spin rounded-full border-2 border-t-2 border-white h-4 w-4"></div>
                ) : (
                  "Check"
                )}
              </div>
            </button>

          </div>
          
          {calculator?.getPanDeclaration?.error?.type && (
            <span className="text-red-200 font-normal text-wrap text-[12px]">
              {calculator?.getPanDeclaration?.error?.type}
            </span>
          )}

          {calculator?.getPanDeclaration?.data?.money_sent_via_moneyhop_in_current_year !== undefined && panNumber.length == 10 && (
            <span className="font-normal relative bottom-2 text-justify text-[14px] mt-4">
              Transactions amounting to ₹{formatNumber(calculator?.getPanDeclaration?.data?.money_sent_via_moneyhop_in_current_year ?? 0)} have already been made by this PAN card holder in the current financial year.
            </span>
          )}

          <div className="flex flex-col items-left mt-4">
            <span className="font-medium text-[16px]">Enter Amount</span>
            <span className="font-normal text-[14px]">
              If you have done any other transaction please enter the amount in INR
            </span>

            {/* FIXED DECLARE AMOUNT ROW */}
            <div
              className={`flex h-10 flex-row items-center w-full mt-2 border border-gray-200 rounded-lg px-2 py-1
                ${calculator?.getPanDeclaration?.data == null ? "bg-gray-100" : "bg-white"}`}
            >
              <input
                className="flex-1 outline-none"
                type="number"
                onChange={(e) => {
                  const value = e.target.value;
                  setDeclareAmount(value);
                }}
                autoComplete="off"
                value={declareAmount}
                disabled={calculator?.getPanDeclaration?.data == null}
              />

              {declareAmount ? (
                <button
                  className="px-1"
                  onClick={() => setDeclareAmount("")}
                >
                  <RoundCloseIcon />
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-row py-5 justify-center">
            <Button
              className={`px-3 py-2 border rounded-lg w-[30%] text-white-100 ${(calculator?.getPanDeclaration?.loading || !calculator?.getPanDeclaration?.data || calculator?.getPanDeclaration?.error) && (!calculator?.amountCalculation?.pan_number) ? "opacity-15": "opacity-100"}`}
              isLoading={false}
              onClickHandler={onSubmitPanDeclaration}
              disabled={(calculator?.getPanDeclaration?.loading || !calculator?.getPanDeclaration?.data || calculator?.getPanDeclaration?.error) && (!calculator?.amountCalculation?.pan_number)}
            >
              {"Submit"}
            </Button>
          </div>

        </div>
      </div>
    </Dialog>
  );
};
